import {
  subcategoriesOf,
  type TaxonomyStatus,
  taxonomyStatus,
} from "@/domains/freelance/taxonomy/store/taxonomy-store";

/**
 * Pure rules of the "Categoria e ambiti" filter field: the draft the user edits
 * in the popover, how it moves, and what the header and hints show. No
 * `@/api/client` and no TanStack here, so the node tests can import it.
 */

/** The taxonomy as the rules read it; the store by default, a fake in tests. */
export type SubcategoryTaxonomy = {
  subcategoriesOf: (category: string) => string[];
  status: () => TaxonomyStatus;
};

const storeTaxonomy: SubcategoryTaxonomy = {
  subcategoriesOf,
  status: taxonomyStatus,
};

/** The popover draft. Category `""` means none. */
export type SubcategoryDraft = { category: string; subcategories: string[] };

/** What a step did that the UI may want to tell the user about. */
export type DraftOutcome =
  | { kind: "removed"; count: number; category: string }
  | { kind: "reset"; hadSubcategories: boolean };

export type DraftStep = {
  next: SubcategoryDraft;
  outcome: DraftOutcome | null;
};

type Surface = "search" | "pool";

const HINT_PREFIX = "company.search.freelancers.result.filters.job_subcategory";

export type BehaviourHintKey =
  | `${typeof HINT_PREFIX}.hint_search`
  | `${typeof HINT_PREFIX}.hint_pool`
  | `${typeof HINT_PREFIX}.hint_whole_category`;

/**
 * Picks a category. The same one changes nothing; another one drops the
 * subcategories and reports how many were removed.
 */
export function chooseCategory(
  draft: SubcategoryDraft,
  key: string,
): DraftStep {
  if (draft.category === key) return { next: draft, outcome: null };
  const count = draft.subcategories.length;
  return {
    next: { category: key, subcategories: [] },
    outcome:
      count > 0 ? { kind: "removed", count, category: draft.category } : null,
  };
}

/**
 * Adds or removes a subcategory. Only children of the draft category can be
 * added; the selection keeps the order of the tree. Removing always works.
 */
export function toggleSubcategory(
  draft: SubcategoryDraft,
  key: string,
  taxonomy: SubcategoryTaxonomy = storeTaxonomy,
): DraftStep {
  if (draft.subcategories.includes(key)) return removeSubcategory(draft, key);
  const children = taxonomy.subcategoriesOf(draft.category);
  if (!children.includes(key)) return { next: draft, outcome: null };
  const subcategories = [...draft.subcategories, key].sort(
    (a, b) => children.indexOf(a) - children.indexOf(b),
  );
  return { next: { ...draft, subcategories }, outcome: null };
}

/** The chip "x": removes the key without checking its parent. */
export function removeSubcategory(
  draft: SubcategoryDraft,
  key: string,
): DraftStep {
  return {
    next: {
      ...draft,
      subcategories: draft.subcategories.filter((s) => s !== key),
    },
    outcome: null,
  };
}

/** "Tutta la categoria": empties the subcategories. Idempotent. */
export function chooseWholeCategory(draft: SubcategoryDraft): DraftStep {
  return { next: { ...draft, subcategories: [] }, outcome: null };
}

/** Clears the draft. */
export function resetDraft(draft: SubcategoryDraft): DraftStep {
  return {
    next: { category: "", subcategories: [] },
    outcome: {
      kind: "reset",
      hadSubcategories: draft.subcategories.length > 0,
    },
  };
}

/**
 * The number on a category header: the selected subcategories, or all the
 * children for "Tutta la categoria". `null` for another category and when the
 * total is unknown (no tree).
 */
export function headerCount(
  draft: SubcategoryDraft,
  category: string,
  taxonomy: SubcategoryTaxonomy = storeTaxonomy,
): number | null {
  if (category !== draft.category) return null;
  if (draft.subcategories.length > 0) return draft.subcategories.length;
  const total = taxonomy.subcategoriesOf(category).length;
  return total > 0 ? total : null;
}

/** The i18n key of the line that explains how the filter behaves. */
export function behaviourHintKey(
  surface: Surface,
  draft: SubcategoryDraft,
): BehaviourHintKey {
  if (draft.subcategories.length === 0)
    return `${HINT_PREFIX}.hint_whole_category`;
  return surface === "search"
    ? `${HINT_PREFIX}.hint_search`
    : `${HINT_PREFIX}.hint_pool`;
}

/**
 * Whether to show the hint that the offer has its own subcategories: filter
 * active, offer with subcategories, same category, and none picked yet.
 */
export function showsOfferSubcategoriesHint({
  active,
  offer,
  draft,
}: {
  active: boolean;
  offer: { category: string; subcategories: string[] } | null;
  draft: SubcategoryDraft;
}): boolean {
  return (
    active &&
    offer !== null &&
    offer.subcategories.length > 0 &&
    offer.category !== "" &&
    draft.category === offer.category &&
    draft.subcategories.length === 0
  );
}

/**
 * Drops the keys that are not children of the category. Without the tree
 * (status not ready) nothing is discarded.
 */
export function keepValidSubcategories(
  category: string | undefined,
  keys: string[],
  taxonomy: SubcategoryTaxonomy = storeTaxonomy,
): string[] {
  if (taxonomy.status() !== "ready") return keys;
  if (!category) return [];
  const children = taxonomy.subcategoriesOf(category);
  return keys.filter((key) => children.includes(key));
}

/** Splits a comma-separated list: trimmed, no empties, no duplicates. */
export function parseSubcategoryCsv(csv?: string): string[] {
  if (!csv) return [];
  return [...new Set(csv.split(",").map((s) => s.trim()))].filter(Boolean);
}

/** Joins keys with commas, cleaned like the parser. `undefined` when empty. */
export function toSubcategoryCsv(keys?: readonly string[]): string | undefined {
  const cleaned = parseSubcategoryCsv(keys?.join(","));
  return cleaned.length > 0 ? cleaned.join(",") : undefined;
}
